"use client";

import { useEffect, useState } from "react";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useStrikes } from "#/hooks/useStrikes";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { nextMaturities } from "#/utils/maturities";

import {
  findClosestATMStrikeIndex,
  showComboWarningToast,
} from "../StrategySelector.utils";

export const findEquidistantStrikes = (
  allStrikes: number[],
  atmIndex: number,
): [number, number, number, number] | null => {
  const n = allStrikes.length;
  // Try pairs below ATM, decrementing from closest to ATM
  for (let j = atmIndex - 1; j > 0; j--) {
    for (let i = j - 1; i >= 0; i--) {
      const putWidth = allStrikes[j] - allStrikes[i];
      // Try all pairs above ATM for call side
      for (let k = atmIndex + 1; k < n - 1; k++) {
        for (let l = k + 1; l < n; l++) {
          const callWidth = allStrikes[l] - allStrikes[k];
          if (putWidth === callWidth) {
            return [allStrikes[i], allStrikes[j], allStrikes[k], allStrikes[l]];
          }
        }
      }
    }
  }

  return null;
};

export const useIronCondor = () => {
  const {
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLegs,
    buildInstrument,
  } = useComboInstrumentsController();
  const [maturityIndex, setMaturityIndex] = useState(0);
  const maturities = nextMaturities();
  const maturity = maturities[maturityIndex];
  const { marketIndexPrice } = useMarketIndexPrice();
  const { strikes } = useStrikes(maturity);

  // Apply Iron Condor: buy lower OTM put, sell OTM put, sell OTM call, buy higher OTM call
  // Note: Users can flip directions to create reverse iron condor (sell-buy-buy-sell pattern)
  const applyIronCondor = () => {
    const atmIndex = findClosestATMStrikeIndex(strikes, marketIndexPrice);
    const equidistantStrikes = findEquidistantStrikes(strikes, atmIndex);
    if (!equidistantStrikes) {
      if (maturityIndex + 1 < maturities.length) {
        setMaturityIndex(maturityIndex + 1);
        return;
      }

      showComboWarningToast();
      return;
    }

    const [K1, K2, K3, K4] = equidistantStrikes;

    handleAddLegs([
      buildInstrument({
        type: "options",
        side: "buy",
        optionType: "put",
        strike: K1,
        size: "1",
        maturity,
      }),
      buildInstrument({
        type: "options",
        side: "sell",
        optionType: "put",
        strike: K2,
        size: "1",
        maturity,
      }),
      buildInstrument({
        type: "options",
        side: "sell",
        optionType: "call",
        strike: K3,
        size: "1",
        maturity,
      }),
      buildInstrument({
        type: "options",
        side: "buy",
        optionType: "call",
        strike: K4,
        size: "1",
        maturity,
      }),
    ]);
  };

  const isActivePatternIronCondor = () => {
    if (comboPerpInstruments.length > 0 || comboOptionInstruments.length !== 4)
      return false;

    const sorted = comboOptionInstruments.toSorted(
      (a, b) => a.strike - b.strike,
    );
    const [a, b, c, d] = sorted;

    // Supports both Iron Condor (buy-sell-sell-buy) and reverse Iron Condor (sell-buy-buy-sell)
    // Structure: alternating sides with put (lowest), put, call, call (highest)
    if (
      a.side === b.side ||
      b.side !== c.side ||
      c.side === d.side ||
      a.optionType !== "put" ||
      b.optionType !== "put" ||
      c.optionType !== "call" ||
      d.optionType !== "call" ||
      a.maturity !== b.maturity ||
      b.maturity !== c.maturity ||
      c.maturity !== d.maturity ||
      a.underlying !== b.underlying ||
      b.underlying !== c.underlying ||
      c.underlying !== d.underlying ||
      a.size !== b.size ||
      b.size !== c.size ||
      c.size !== d.size
    ) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (maturityIndex > 0) {
      // Retry Iron Condor on maturity change to find equidistant strikes for the next available maturity
      applyIronCondor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only rerun when maturityIndex changes to avoid infinite loop
  }, [maturityIndex]);

  return { applyIronCondor, isActivePatternIronCondor };
};
